import { useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { updateSession, updateWorkingSession } from '../../../../../store/actions/admin/create-event/session';

import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { Session } from '../../../../../types/working-model';
import { OptionalComponent } from '../../../../../utils/optional-component';
import ColorEditor from '../../settings/design/colors/color-editor';
import Icon, { ICONS } from '../../../../general-ui/icon';
import ModalComponent from '../../../../general-ui/modal/modal';

import './editor-banner-color.scss';

interface ISaveOptions {
	reset: boolean;
}

const EditBannerColor: React.FC = () => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const colors = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.design?.colors?.colors);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);

	const streamPlaceholderBGColor = workingSession?.layout?.styling_overrides?.content?.stream_placeholder_background_color;
	const secondaryBackgroundColor = colors?.secondaryBackgroundColor;

	const [showColorModal, setShowColorModal] = useState(false);
	// selected db value || --secondaryBackgroundColor || ['#ffffff], 1
	const [selectedColor, setSelectedColor] = useState<[string, number]>(streamPlaceholderBGColor || secondaryBackgroundColor || ['#ffffff', 1]);

	const dispatch = useDispatch();

	const handleShowModal = () => {
		setShowColorModal(true);
	};

	const handleCancel = () => {
		setShowColorModal(false);
	};

	const handleSave = (saveOptions?: ISaveOptions) => () => {
		if (!token || !workingSession) return;

		const reset = saveOptions?.reset;
		if (reset) {
			if (!secondaryBackgroundColor) {
				return;
			}
			setSelectedColor(secondaryBackgroundColor);
		}

		const updatedWorkingSession: Session = {
			...workingSession,
			layout: {
				...(workingSession.layout ?? {}),
				styling_overrides: {
					...(workingSession.layout?.styling_overrides ?? {}),
					content: {
						...(workingSession.layout?.styling_overrides?.content ?? {}),
						stream_placeholder_background_color: selectedColor,
					}
				}
			}
		};

		if (reset) {
			// delete it entirely so that if the user changes themes, the default color properly updates in the live event
			delete updatedWorkingSession.layout.styling_overrides?.content.stream_placeholder_background_color;
		}

		batch(() => {
			// update db
			dispatch(updateSession(updatedWorkingSession, token));
			// update redux
			dispatch(updateWorkingSession(updatedWorkingSession));
		});

		handleCancel();
	};

	if (!token) return null;
	return (
		<>
			<button
				className="clear no-style round edit-session-stream-bg-color"
				onClick={handleShowModal}
			>
				<Icon name={ICONS.PAINTBRUSH} color="" size={22} />
			</button>

			<ModalComponent
				open={showColorModal}
				closeable={false}
				cancellable={true}
				className="edit-banner-color-modal"
				title='Edit banner background color'
				onRequestClose={handleCancel}
				showOverflow
				footer={
					<>
						<button className='button' onClick={handleCancel}>Cancel</button>
						<button className='button save primary' onClick={handleSave()}>Save</button>
					</>
				}
			>
				<div>
					<ColorEditor
						color={selectedColor}
						title={'Background Color'}
						onChange={(hex: string, opacity: number) => {
							setSelectedColor([hex, opacity]);
						}}
						disableOpacity
					/>
					<OptionalComponent display={!!(secondaryBackgroundColor?.[0] && selectedColor[0] !== secondaryBackgroundColor[0])}>
						<button
							className="clear no-style text-only-primary restore-default-session-stream-bg"
							onClick={handleSave({ reset: true })}
						>Restore default</button>
					</OptionalComponent>
				</div>
			</ModalComponent>
		</>
	);
};

export default EditBannerColor;
